import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import { useGlobalCore } from '../../../../../core/modules/global';
import { UPDATE_TYPE_DIALOG, CREATE_PRODUCT_ATTRIBUTE_DIALOG, UPDATE_PRODUCT_ATTRIBUTE_DIALOG } from '../../../../../core/constants/dialog';
import Spinner from '../../../Spinner';
import { tType, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import Swal from 'sweetalert2';
import { PutTypePayloadRequest } from '../../../../../core/modules/master/interfaces/put-type';
import { TypeLabel } from '../categories/CreateDialog';
import { ERROR } from '../../../../../core/constants/errorCode';

interface Props {
  type: tType;
}

const schema = yup.object().shape({
  name: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),
  requires_cost: yup.boolean().optional(),
  requires_commission: yup.boolean().optional(),
});

const UpdateDialog = (props: Props) => {
  const { type } = props;
  const { dialogs, closeDialog } = useGlobalCore();
  const { type: selectedType, errorCode, putType, deleteType, getTypes, setType } = useMasterCore();
  const { companyId } = useCompanyCore();

  const [loading, setLoading]  = useState(false);

  const { control, formState, handleSubmit, resetField, setValue } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const { isValid, errors } = formState;

  const resetForm = useCallback(() => {
    resetField('name');
    resetField('description');
    resetField('requires_cost');
    resetField('requires_commission');
    setType(null);
    closeDialog(UPDATE_TYPE_DIALOG);
  }, [closeDialog, resetField, setType]);

  useEffect(() => {
    if (selectedType) {
      setValue("name", selectedType.name);
      setValue("description", selectedType.description);
      setValue("requires_cost", selectedType.requires_cost ?? false);
      setValue("requires_commission", selectedType.requires_commission ?? false);
    }
  }, [selectedType, setValue]);

  const onSubmit = useCallback(
    (values: PutTypePayloadRequest) => {
      if (companyId && selectedType) {
        setLoading(true);
        putType(companyId, selectedType.id, values, type, () => {
          resetForm();
          getTypes(companyId, type);
          setLoading(false);
        }, () => {
          setLoading(false);
        });
      }
    },
    [companyId, selectedType, type, putType, resetForm, getTypes]
  );

  const onDelete = useCallback(() => {
    Swal.fire({
      title: "Confimar acción",
      text: `Eliminar el tipo ${selectedType?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si, eliminar!",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed && companyId && selectedType) {
        setLoading(true);

        deleteType(
          companyId,
          selectedType.id,
          type,
          () => {
            setLoading(false);
            getTypes(companyId, type);
            resetForm();
          },
          () => {
            setLoading(false);
          }
        );
      }
    });
  }, [companyId, type, deleteType, getTypes, resetForm, selectedType]);

  return (
    <Dialog
      classes={{
        paper: 'm-8 sm:m-24 min-w-[350px]',
      }}
      onClose={resetForm}
      open={Array.isArray(dialogs) && dialogs.includes(UPDATE_TYPE_DIALOG)}
    >
      <DialogContent>
        <div className="flex flex-auto flex-col py-16 px-0 sm:px-16">
          <div className="flex flex-col">
            <div className="mb-16 flex items-center">
              <Typography className="text-2xl font-bold truncate">
                Modificar Tipo de {TypeLabel[type]}
              </Typography>
            </div>
      
            <div className="w-full max-w-3xl mt-20">
              <div className="flex flex-col space-y-32">
                <form noValidate onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-24">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Nombre"
                          type="text"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.name}
                          helperText={errors?.name?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                  </div>

                  <div className="mb-24">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descripción"
                          type="textarea"
                          variant="outlined"
                          disabled={loading}
                          error={!!errors.description}
                          helperText={errors?.description?.message}
                          required
                          fullWidth
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();
                            field.onChange(value);
                          }}
                          value={field.value || ''}
                        />
                      )}
                    />
                  </div>

                  {/* Solo mostrar los checkboxes para tipos de adquisición */}
                  {type === 'acquisition' && (
                    <>
                      <div className="mb-24">
                        <Controller
                          name="requires_cost"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value ?? false}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                />
                              }
                              label="Requiere Costo"
                            />
                          )}
                        />
                      </div>

                      <div className="mb-24">
                        <Controller
                          name="requires_commission"
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value ?? true}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                />
                              }
                              label="Requiere Comisión"
                            />
                          )}
                        />
                      </div>
                    </>
                  )}

                  {errorCode === ERROR.ERR_409 && (
                    <Typography color="error" className="text-md text-center mb-24">
                      Ya existe un tipo con ese nombre
                    </Typography>
                  )}

                  <div className="flex justify-center gap-4">
                    <Button
                      variant="contained"
                      color="error"
                      sx={{ borderRadius: 50 }}
                      disabled={loading}
                      type="button"
                      onClick={onDelete}
                    >
                      <Spinner show={loading} />
                      Eliminar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ borderRadius: 50 }}
                      disabled={!isEmpty(errors) || !isValid || loading || (Array.isArray(dialogs) && (dialogs.includes(CREATE_PRODUCT_ATTRIBUTE_DIALOG) || dialogs.includes(UPDATE_PRODUCT_ATTRIBUTE_DIALOG)))}
                      type="submit"
                      size="large"
                    >
                      <Spinner show={loading} />
                      Guardar
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateDialog;
